import { useState, type CSSProperties, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toFieldErrors } from '@/shared/api/submit';
import { fetchActiveCountries } from '@/shared/api/places';
import { toOptions } from '@/shared/lib/options';
import { getListRows, getTotalPages } from '@/shared/lib/pagination';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { Pagination } from '@/shared/ui/Pagination';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { fetchCities, fetchRegions } from '../users/api';
import {
  SECTION_STATUS,
  fetchManagers,
  fetchVerifyList,
  verifyRequest,
  type SectionStatus,
  type VerificationRequest,
} from './api';
import { useCanEditVerification } from './useCanEditVerification';

const FILTER_KEYS = ['request_id', 'service_id', 'country_id', 'region_id', 'city_id', 'manager_id'] as const;
type Filters = Record<(typeof FILTER_KEYS)[number], string>;

const TABS = ['Все заявки', 'Верифицированные'];

const labelStyle: CSSProperties = { display: 'block', fontWeight: 600, marginBottom: 4 };

const readFilters = (params: URLSearchParams): Filters =>
  Object.fromEntries(FILTER_KEYS.map((key) => [key, params.get(key) ?? ''])) as Filters;

const SECTIONS = [
  { path: 'contacts', key: 'contacts' },
  { path: 'specialization', key: 'specialization' },
  { path: 'legal', key: 'legal_data' },
  { path: 'photo', key: 'photo_video' },
] as const;

const SectionCell = ({ status, to }: { status: SectionStatus; to: string }) => {
  const statusId = Number(status.status_id);
  const verifier = status.verifier?.name ?? '';

  if (statusId === SECTION_STATUS.WAITING) return <Link to={to}>Проверить</Link>;
  if (statusId === SECTION_STATUS.REJECT) {
    return (
      <Link to={to}>
        <div className="text-red">Отклонено:</div>
        <div>{verifier}</div>
      </Link>
    );
  }
  if (statusId === SECTION_STATUS.VERIFY) {
    return (
      <Link to={to}>
        <div>Проверено:</div>
        <div>{verifier}</div>
      </Link>
    );
  }
  return null;
};

const isFullyChecked = (request: VerificationRequest) =>
  SECTIONS.every(({ key }) => Number(request[key].status_id) === SECTION_STATUS.VERIFY);

export const VerificationPage = () => {
  const qc = useQueryClient();
  const canVerify = useCanEditVerification();
  const [params, setParams] = useSearchParams();
  const [rowErrors, setRowErrors] = useState<Record<number, string[]>>({});

  const verified = params.get('verified') === '1' ? 1 : 0;
  const page = Math.max(1, Number(params.get('page')) || 1);
  const applied = readFilters(params);
  const [draft, setDraft] = useState<Filters>(applied);

  const updateParams = (patch: Record<string, string>) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([key, value]) => (value ? next.set(key, value) : next.delete(key)));
      return next;
    });

  const setDraftField = (patch: Partial<Filters>) => setDraft((prev) => ({ ...prev, ...patch }));

  const countriesQuery = useQuery({ queryKey: ['active-countries'], queryFn: fetchActiveCountries });
  const regionsQuery = useQuery({
    queryKey: ['regions', draft.country_id],
    queryFn: () => fetchRegions(draft.country_id),
    enabled: Boolean(draft.country_id),
  });
  const citiesQuery = useQuery({
    queryKey: ['cities', draft.region_id],
    queryFn: () => fetchCities(draft.region_id),
    enabled: Boolean(draft.region_id),
  });
  const managersQuery = useQuery({ queryKey: ['verification', 'managers'], queryFn: fetchManagers });

  const listParams = {
    page,
    verified_only: verified,
    request_id: applied.request_id,
    service_id: applied.service_id,
    city_id: applied.city_id,
    service_operator_id: applied.manager_id,
  };
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['verification', 'list', listParams],
    queryFn: () => fetchVerifyList(listParams),
  });
  const rows = getListRows<VerificationRequest>(data, ['requests']);

  const verify = useMutation({
    mutationFn: verifyRequest,
    onMutate: (id) => setRowErrors((prev) => ({ ...prev, [id]: [] })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['verification', 'list'] }),
    onError: (err, id) =>
      setRowErrors((prev) => ({ ...prev, [id]: Object.values(toFieldErrors(err)).flat() })),
  });

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    updateParams({ ...draft, page: '' });
  };

  const onReset = (e: FormEvent) => {
    e.preventDefault();
    const empty = Object.fromEntries(FILTER_KEYS.map((key) => [key, ''])) as Filters;
    setDraft(empty);
    updateParams({ ...empty, page: '' });
  };

  const selectTab = (tab: number) => updateParams({ verified: tab ? '1' : '', page: '' });

  return (
    <>
      <section className="content-header">
        <h1>Верификация</h1>
        <form onSubmit={onSearch} style={{ marginTop: 12 }}>
          <div className="row">
            <div className="col-lg-3">
              <div className="form-group">
                <span style={labelStyle}>ID запроса</span>
                <input
                  className="form-control input-sm"
                  placeholder="ID запроса"
                  value={draft.request_id}
                  onChange={(e) => setDraftField({ request_id: e.target.value.replace(/\D/g, '') })}
                />
              </div>
              <div className="form-group">
                <span style={labelStyle}>ID СТО</span>
                <input
                  className="form-control input-sm"
                  placeholder="ID СТО"
                  value={draft.service_id}
                  onChange={(e) => setDraftField({ service_id: e.target.value.replace(/\D/g, '') })}
                />
              </div>
            </div>
            <div className="col-lg-3">
              <div className="form-group">
                <span style={labelStyle}>Менеджер</span>
                <FilterSelect
                  options={toOptions(managersQuery.data)}
                  value={draft.manager_id}
                  isLoading={managersQuery.isLoading}
                  placeholder="Менеджер"
                  onChange={(manager_id) => setDraftField({ manager_id })}
                />
              </div>
            </div>
            <div className="col-lg-3">
              <div className="form-group">
                <span style={labelStyle}>Страна</span>
                <FilterSelect
                  options={toOptions(countriesQuery.data)}
                  value={draft.country_id}
                  isLoading={countriesQuery.isLoading}
                  placeholder="Страна"
                  onChange={(country_id) => setDraftField({ country_id, region_id: '', city_id: '' })}
                />
              </div>
              <div className="form-group">
                <span style={labelStyle}>Регион</span>
                <FilterSelect
                  options={toOptions(regionsQuery.data)}
                  value={draft.region_id}
                  isDisabled={!draft.country_id}
                  isLoading={regionsQuery.isFetching}
                  placeholder="Регион"
                  onChange={(region_id) => setDraftField({ region_id, city_id: '' })}
                />
              </div>
              <div className="form-group">
                <span style={labelStyle}>Город</span>
                <FilterSelect
                  options={toOptions(citiesQuery.data)}
                  value={draft.city_id}
                  isDisabled={!draft.region_id}
                  isLoading={citiesQuery.isFetching}
                  placeholder="Город"
                  onChange={(city_id) => setDraftField({ city_id })}
                />
              </div>
            </div>
          </div>
          <button type="button" className="btn btn-warning" onClick={onReset} disabled={isFetching}>
            Очистить все поля
          </button>{' '}
          <button type="submit" className="btn btn-primary" disabled={isFetching}>
            Поиск
          </button>
        </form>
      </section>
      <section className="content">
        <ul className="nav nav-tabs">
          {TABS.map((title, tab) => (
            <li key={title} className={tab === verified ? 'active' : ''}>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  selectTab(tab);
                }}
              >
                {title}
              </a>
            </li>
          ))}
        </ul>
        <div className="box">
          <div className="box-header with-border">
            <h3 className="box-title">Верификация данных СТО</h3>
          </div>
          <div className="box-body">
            <QueryStatus isLoading={isLoading} error={error} errorText="Ошибка загрузки заявок" />
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>ID запроса</th>
                    <th>Дата</th>
                    <th>ID СТО</th>
                    <th>Название СТО</th>
                    <th>Город</th>
                    <th>Менеджер</th>
                    <th>Контакты</th>
                    <th>Специализация</th>
                    <th>Юр. информация</th>
                    <th>Фото/Видео</th>
                    {!verified && <th>Статус</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((request) => (
                    <tr
                      key={request.id}
                      style={
                        Number(request.status_id) === SECTION_STATUS.REJECT
                          ? { background: 'rgba(255, 0, 0, 0.2)' }
                          : undefined
                      }
                    >
                      <td>{request.id}</td>
                      <td>{new Date(Number(request.create_time) * 1000).toLocaleDateString('ru-RU')}</td>
                      <td>{request.service.id}</td>
                      <td>{request.service.name}</td>
                      <td>{request.city}</td>
                      <td>{request.service_operator?.name}</td>
                      {SECTIONS.map(({ path, key }) => (
                        <td key={path}>
                          <SectionCell status={request[key]} to={`/verification/${path}/${request.id}`} />
                        </td>
                      ))}
                      {!verified && (
                        <td>
                          {canVerify && isFullyChecked(request) && (
                            <button
                              type="button"
                              className="btn btn-success"
                              disabled={verify.isPending}
                              onClick={() => verify.mutate(request.id)}
                            >
                              Верифицировать
                            </button>
                          )}
                          {(rowErrors[request.id] ?? []).map((message) => (
                            <div key={message} className="text-red">
                              {message}
                            </div>
                          ))}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <Pagination
            page={page}
            totalPages={getTotalPages(data)}
            onChange={(next) => {
              updateParams({ page: String(next) });
              window.scrollTo(0, 0);
            }}
          />
        </div>
      </section>
    </>
  );
};
